import { useQuery } from "@apollo/client";
import { useState, useEffect } from "react";
import {
  DashboardSkeleton,
  ErrorState,
} from "../../components/states";
import Breadcrumb from "../../components/Breadcrumb";
import Card from "../../components/Card";
import { useParams, useNavigate } from "react-router-dom";
import { useToast } from "../../components/Toast/toastHook";
import MobileCard from "../../components/dashboard/MobileCard";
import FavoriteButton from "../../components/FavoriteButton";
import {
  GET_AGENT_LISTINGS,
  GET_AGENT_BUYERS,
} from "../../graphql/agentQueries";
import { Table } from "antd";
import priceFormat from "../../utils/priceFormat";
import fullDate from "../../utils/fullDate";

const AgentDashboard = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const toast = useToast(5000);

  // Get current user
  const currentUser = JSON.parse(localStorage.getItem("userInfo"));

  const [stats, setStats] = useState({
    expiredListings: 0,
    activeListings: 0,
    allListings: 0,
  });

  // Get agent's listings
  const {
    loading: listingsLoading,
    error: listingsError,
    data: listingsData,
    refetch: refetchListings,
  } = useQuery(GET_AGENT_LISTINGS, {
    fetchPolicy: "network-only",
  });

  // Get agent's buyers
  const {
    loading: buyersLoading,
    error: buyersError,
    data: buyersData,
    refetch: refetchBuyers,
  } = useQuery(GET_AGENT_BUYERS, {
    variables: { userId: currentUser?.id },
    fetchPolicy: "network-only",
    skip: !currentUser?.id,
  });

  useEffect(() => {
    if (listingsData) {
      const listings = listingsData.getUserCompanyListings || [];
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const activeListings = listings.filter((listing) => {
        if (!listing.expiration) return false;
        const expirationDate = new Date(parseInt(listing.expiration) || listing.expiration);
        expirationDate.setHours(0, 0, 0, 0);
        return expirationDate >= today;
      });

      const expiredListings = listings.filter((listing) => {
        if (!listing.expiration) return false;
        const expirationDate = new Date(parseInt(listing.expiration) || listing.expiration);
        expirationDate.setHours(0, 0, 0, 0);
        return expirationDate < today;
      });

      setStats({
        expiredListings: expiredListings.length,
        activeListings: activeListings.length,
        allListings: listings.length,
      });
    }

    if (listingsError) {
      console.error(listingsError);
      toast("error", `${listingsError.name}: ${listingsError.message}`);
    }
    if (buyersError) {
      console.error(buyersError);
      toast("error", `${buyersError.name}: ${buyersError.message}`);
    }
  }, [listingsData, buyersData, listingsError, buyersError]);

  const recentListings = (listingsData?.getUserCompanyListings || []).slice(
    0,
    5
  );

  const recentBuyers = (buyersData?.getUserBuyers || []).slice(0, 5);

  const listingColumns = [
    {
      title: "Address",
      dataIndex: "streetAddr",
      key: "streetAddr",
      render: (text, record) => (
        <div className="text-black flex-col">
          <div>{`${text},`}</div>
          <div>{`${record.city}, ${record.state}, ${record.zip}`}</div>
        </div>
      ),
    },
    {
      title: "Price",
      dataIndex: "price",
      key: "price",
      render: (price) => (
        <span className="font-medium text-black">${priceFormat(price)}</span>
      ),
    },
    {
      title: "Agent",
      dataIndex: "user",
      key: "agent",
      render: (user) => (
        <span className="font-medium text-black">{`${user?.firstName} ${user?.lastName}`}</span>
      ),
    },
    {
      title: "Live Date",
      dataIndex: "expiration",
      key: "expiration",
      render: (expiration) => (
        <span className="font-medium text-black">{fullDate(expiration)}</span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <div className="flex items-center space-x-3.5">
          <FavoriteButton
            listingId={record._id}
            agentId={currentUser?.id}
            companyId={companyId}
          />
          <button
            className="hover:text-primary"
            onClick={() =>
              navigate(`/company/${companyId}/agent/listings/${record._id}/view`)
            }
            title="View"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              className="w-5 h-5"
            >
              <path d="M10 12.5a2.5 2.5 0 100-5 2.5 2.5 0 000 5z" />
              <path
                fillRule="evenodd"
                d="M.664 10.59a1.651 1.651 0 010-1.186A10.004 10.004 0 0110 3c4.257 0 7.893 2.66 9.336 6.41.147.381.146.804 0 1.186A10.004 10.004 0 0110 17c-4.257 0-7.893-2.66-9.336-6.41zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>
      ),
    },
  ];

  const buyerColumns = [
    {
      title: "Name",
      key: "name",
      render: (_, record) => (
        <span className="font-medium text-black">
          {record.firstName} {record.lastName}
        </span>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (email) => email || "-",
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      render: (phone) => phone || "-",
    },
    {
      title: "Status",
      key: "status",
      render: (_, record) => (
        <div className="flex gap-1">
          {record.pre_qualified && (
            <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">Pre-Qualified</span>
          )}
          {record.first_time_buyer && (
            <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">First Time</span>
          )}
        </div>
      ),
    },
  ];

  if (listingsLoading || buyersLoading)
    return (
      <div className="">
        <Breadcrumb pageName={"Dashboard"} />
        <DashboardSkeleton tileRows={[3]} tableCards={2} />
      </div>
    );

  if ((listingsError || buyersError) && !listingsData && !buyersData)
    return (
      <div className="">
        <Breadcrumb pageName={"Dashboard"} />
        <ErrorState
          onRetry={() => {
            refetchListings();
            refetchBuyers();
          }}
        />
      </div>
    );

  return (
    <div className="">
      <Breadcrumb pageName={"Dashboard"} />
      <div className="flex flex-col gap-6">
        {/* Stats Cards */}
        <Card>
          <div className="p-6">
            <div className="border-b-2 border-primary pb-2 mb-6">
              <h1 className="text-3xl font-semibold text-secondary">
                My Overview
              </h1>
            </div>
            <div className="flex flex-row gap-4">
              <MobileCard text="All My Listings" value={stats.allListings} />
              <MobileCard text="My Active Listings" value={stats.activeListings} />
              <MobileCard text="My Expired Listings" value={stats.expiredListings} />
            </div>
          </div>
        </Card>

        {/* Recent Listings */}
        <Card>
          <div className="p-6">
            <div className="border-b-2 border-primary pb-2 mb-6 flex justify-between items-center">
              <h2 className="text-2xl font-semibold text-secondary">
                Recent Listings
              </h2>
              <button
                onClick={() => navigate(`/company/${companyId}/agent/listings`)}
                className="text-primary hover:underline"
              >
                View All
              </button>
            </div>
            <Table
              dataSource={recentListings}
              columns={listingColumns}
              rowKey="_id"
              pagination={false}
              locale={{ emptyText: "No listings yet" }}
              onRow={(record) => ({
                onClick: () => navigate(`/company/${companyId}/agent/listings/${record._id}/view`),
                style: { cursor: "pointer" },
              })}
              rowClassName="hover:bg-gray-50 transition-colors"
            />
          </div>
        </Card>

        {/* Recent Buyers */}
        <Card>
          <div className="p-6">
            <div className="border-b-2 border-primary pb-2 mb-6 flex justify-between items-center">
              <h2 className="text-2xl font-semibold text-secondary">
                Recent Buyers
              </h2>
              <button
                onClick={() =>
                  navigate(`/company/${companyId}/agent/buyers`)
                }
                className="text-primary hover:underline"
              >
                View All Buyers
              </button>
            </div>
            <Table
              dataSource={recentBuyers}
              columns={buyerColumns}
              rowKey="_id"
              pagination={false}
              locale={{ emptyText: "No buyers yet" }}
            />
          </div>
        </Card>
      </div>
    </div>
  );
};

export default AgentDashboard;
